/**
 * Module dependencies.
 */

import { Balloon } from 'src/core/advisor-config/balloons';
import { Image, Type } from 'src/components/core';
import { ReactElement } from 'react';
import { color, media, units } from '@untile/react-components';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = Balloon & {
  className?: string;
};

/**
 * `StyledImage` styled component.
 */

const StyledImage = styled(Image)`
  transition: transform 0.3s ease;
  will-change: transform;
`;

/**
 * `Name` styled component.
 */

const Name = styled(Type.H4)`
  padding: 0 ${units(1)};
  transition: transform 0.4s ease;
`;

/**
 * `Card` styled component.
 */

const Card = styled.a`
  color: ${color('grey800')};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  outline: none;
  position: relative;
  text-decoration: none;

  &:focus,
  &:hover {
    ${StyledImage} {
      transform: scale(0.98);
    }

    ${Name} {
      transform: scale(0.965);
    }
  }
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  background-color: ${color('white')};
  margin-bottom: ${units(1)};
  overflow: hidden;
  padding-bottom: 100%;
  position: relative;

  ${media.min('md')`
    margin-bottom: ${units(2)};
  `}
`;

/**
 * `BalloonCard` component.
 */

const BalloonCard = (props: Props): ReactElement => {
  const { className, image, name, url } = props;

  return (
    <Card
      className={className}
      href={url}
      rel={'noopener'}
      target={'_blank'}
    >
      <ImageWrapper>
        <StyledImage
          alt={name}
          fill
          src={image}
          style={{ objectFit: 'cover' }}
        />
      </ImageWrapper>

      <Name>{name}</Name>
    </Card>
  );
};

/**
 * Export `BalloonCard` component.
 */

export default BalloonCard;
